/**
 * Admin i18n — a lightweight, self-contained translation system used only
 * by /admin routes. It is intentionally decoupled from the public-facing
 * i18n (which lives in /app/frontend/src/i18n/translations.js) so that:
 *   • Changing the shop language never touches the admin language.
 *   • The admin can force `dir="rtl"` on the admin surface only.
 *   • The 16-language public dictionary and this 3-language admin dictionary
 *     evolve independently.
 *
 * Only three languages are exposed here per product requirement:
 *   en — English (default)
 *   de — Swiss Standard German (always "ss", never "ß")
 *   ar — Arabic (RTL)
 *
 * Persistence: localStorage["admin_language"]. Falls back to "en".
 * Fallback chain when a key is missing: requested lang → en → the raw key.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export const ADMIN_LANGS = [
  { code: "en", label: "EN", full: "English", dir: "ltr" },
  { code: "de", label: "DE", full: "Deutsch", dir: "ltr" },
  { code: "ar", label: "AR", full: "العربية", dir: "rtl" },
];

const LS_KEY = "admin_language";

/* ─────────────────────────── translations ─────────────────────────── */

const en = {
  common: {
    admin: "Admin", back_to_site: "Back to site", logout: "Log out",
    cancel: "Cancel", save: "Save", saving: "Saving…", delete: "Delete",
    edit: "Edit", close: "Close", add: "Add", remove: "Remove",
    apply: "Apply", refresh: "Refresh", search: "Search…", actions: "Actions",
    status: "Status", active: "Active", hidden: "Hidden", published: "Published",
    archived: "Archived", loading: "Loading…", no_data: "—", empty: "—",
    yes: "Yes", no: "No", confirm: "Confirm", name: "Name", email: "Email",
    phone: "Phone", price: "Price", total: "Total", quantity: "Quantity",
    note: "Note", optional: "optional", required: "Required", all: "All",
    approve: "Approve", hide: "Hide", show: "Show", toggle: "Toggle",
    duplicate: "Duplicate", copy: "Copy", updated: "Updated", created: "Created",
    deleted: "Deleted", error: "Error", failed: "Failed",
    failed_to_load: "Failed to load", failed_to_update: "Failed to update",
    save_failed: "Save failed", delete_failed: "Delete failed",
    load_failed: "Load failed", operation_failed: "Operation failed",
    del: "Del", details: "Details", back: "Back", new: "New",
  },
  login: {
    heading: "Coiffeur Tattoo 01&Co.", eyebrow: "Admin",
    email_placeholder: "Email", password_placeholder: "Password",
    submit: "Login", loading: "…", not_admin: "Not an admin",
    generic_error: "Error",
  },
  tabs: {
    analytics: "Analytics", orders: "Orders", appointments: "Appointments",
    products: "Products", categories: "Categories", inventory: "Inventory",
    customers: "Customers", reviews: "Reviews", questions: "Q&A",
    discounts: "Discounts", consultations: "Consultations", basra: "Basra",
    returns: "Returns",
  },
  analytics: {
    revenue_chf: "Revenue CHF", orders: "Orders", avg_order: "Avg. order",
    customers: "Customers", page_views: "Page views", add_to_cart: "Add to cart",
    checkout_starts: "Checkout starts", total_events: "Total events",
    revenue_by_country: "Revenue by country", revenue_by_currency: "Revenue by currency",
  },
  orders: {
    order: "Order", email: "Email", country: "Country", status: "Status",
    total_chf: "Total CHF", return: "Return", actions: "Actions",
    pledge: "Pledge", tracking_title: "Set tracking",
    tracking_prompt_carrier: "Carrier (e.g. Swiss Post):",
    tracking_prompt_number: "Tracking number:",
    tracking_prompt_url: "Tracking URL:",
    refund: "Refund", refund_confirm: "Refund this order?",
    refund_success: "Refunded", refund_error: "Refund failed",
    empty: "No orders yet.",
  },
  appointments: {
    title: "Appointments", legend: "🟢 confirmed · 🔴 cancelled · 🟠 no-show",
    block_slot: "Block time",
    blocked_times: "Blocked times", when: "When", customer: "Customer",
    service: "Service", price: "Price", empty: "No appointments",
    extend_confirm: "Extend appointment by 30 minutes and notify customer by email?",
    extended: "Appointment extended",
    noshow_confirm: "Mark as \"No-Show\" and charge 50% fee (CHF {fee})?",
    marked_noshow: "Marked as No-Show", extend_30: "+30 min", no_show: "No-Show",
    block_prompt_start: "Start time to block (ISO, e.g. 2026-09-14T14:15:00+01:00):",
    block_prompt_note: "Note:", block_default_note: "Blocked",
    block_success: "Time blocked",
    unblock_confirm: "Release this block?", unblocked: "Released",
    error: "Error",
  },
  products: {
    title: "Products", search: "Search…", new: "New product",
    col_slug: "Slug", col_name_de: "Name (DE)", col_category: "Category",
    col_price: "Price", col_variants: "Variants · Stock", col_status: "Status",
    col_actions: "Actions", needs_setup: "needs setup", no_results: "No products match your search.",
    duplicate: "Duplicate", edit: "Edit", delete: "Delete",
    hide: "Hide", show: "Show",
    duplicate_confirm: "Duplicate \"{slug}\"? The copy will be hidden and stock reset.",
    duplicated: "Duplicated → {slug}", duplicate_failed: "Duplicate failed",
    delete_confirm: "Delete \"{slug}\"? Products referenced by orders will be archived, not deleted.",
    archived_msg: "Archived (had order history)", deleted: "Deleted",
    updated: "Product updated", created_reminder: "Product created — remember to set stock on the inventory tab",
    slug_required: "Slug is required",
    section_info: "Product information", section_prices: "Prices",
    section_translations: "Translations (name + description)",
    section_variants: "Variants / sizes", section_images: "Images (upload from your computer)",
    section_seo: "SEO (per language)", section_publishing: "Publishing",
    field_slug: "Slug (URL identifier)", field_category: "Category",
    field_sku: "SKU", field_weight: "Weight (g)",
    field_regular: "Regular price (CHF)", field_sale: "Sale price (CHF, optional)",
    field_discount: "Discount % (auto)", discount_none: "No discount",
    field_sale_starts: "Discount starts (optional)", field_sale_ends: "Discount ends (optional)",
    field_name: "Name", field_desc: "Description (Markdown supported)",
    desc_placeholder: "**Bold**, *italic*, - lists, links — everything renders on the product page.",
    add_size: "+ Add size", add_size_prompt: "Variant / size label (e.g. 100ml)",
    stock_hint: "Stock per variant is managed via the \"Stock\" buttons on the products list — every change is recorded in the inventory ledger.",
    field_seo_title: "SEO title", field_seo_desc: "SEO description",
    checkbox_published: "Published (visible in shop)", checkbox_featured: "Featured",
    checkbox_bestseller: "Bestseller", checkbox_new: "New",
    eyebrow_new: "New product", eyebrow_editing: "Editing", untitled: "Untitled",
    btn_create: "Create product", btn_save: "Save changes",
    new_category_btn: "+ New", new_category_prompt: "New category name (German):",
    category_created: "Category created", category_create_failed: "Create failed",
    stock_dialog_title: "Stock · {name}", stock_variant: "Variant",
    stock_current: "Current", stock_mode_sale: "Store sale", stock_mode_plus: "Add +",
    stock_mode_minus: "Remove −", stock_mode_set: "Set exact",
    stock_qty: "Quantity", stock_note_placeholder: "Shipment arrived, damage, recount…",
    stock_note: "Note (optional)", stock_apply: "Apply",
    inventory_updated: "Inventory updated",
  },
  categories: {
    title: "Categories", new: "New category",
    col_image: "Image", col_slug: "Slug", col_name_de: "Name (DE)",
    col_position: "Position", col_status: "Status", col_actions: "Actions",
    empty: "No categories yet — create one.",
    delete_confirm: "Delete \"{slug}\"? (blocked if products still use it)",
    updated: "Updated", created: "Created", deleted: "Deleted",
    form_new: "New category", form_edit: "Edit category",
    field_slug: "Slug (URL)", field_name_placeholder: "Name",
    field_desc_placeholder: "Description (optional)", field_image: "Category image",
    field_active: "Active", field_position: "Position",
    slug_placeholder: "natural-soaps",
  },
  inventory: {
    title: "Inventory",
    subtitle: "Unified across online + in-store. Every change is written to the audit ledger.",
    refresh: "Refresh",
    products_count: "Products ({n})",
    alerts_badge: "Alerts", alerts_show_all: "Show all products",
    alerts_show_only: "Show only low/critical/out variants",
    empty_all_ok: "No variants currently in alert state 🎉", empty_none: "No products",
    setup: "setup",
    choose_prompt: "Choose a product & variant on the left",
    selected: "Selected", variant: "Variant", current_stock: "Current stock",
    action_store_sale: "Store sale", hint_store_sale: "Physical shop",
    action_add_stock: "Add stock", hint_add_stock: "Restock, shipment",
    action_remove_stock: "Remove stock", hint_remove_stock: "Damage, write-off",
    action_set_exact: "Set exact", hint_set_exact: "Recount, correction",
    visibility_hint: "Real stock numbers are shown only in the Admin panel. Customers see a state (in / low / out) — never the exact count above 4.",
    movement_history: "Movement history", filter: "Filter:", all_products: "All products",
    all_reasons: "All reasons",
    reason_online: "Online order", reason_store: "Store sale", reason_added: "Stock added",
    reason_removed: "Stock removed", reason_adjust: "Adjustment", reason_return: "Return",
    reason_other: "Other",
    col_when: "When", col_product: "Product", col_variant: "Variant",
    col_before: "Before", col_delta: "Δ", col_after: "After",
    col_reason: "Reason", col_source: "Source", col_ref_admin: "Ref / Admin",
    col_note: "Note", no_movements: "No movements recorded yet.",
    deleted_product: "deleted",
    pill_online: "Online", pill_store: "Store", pill_added: "Added",
    pill_removed: "Removed", pill_adjust: "Adjust", pill_return: "Return",
    pill_other: "Other",
    dialog_sale_title: "Register in-store sale", dialog_sale_cta: "Record sale",
    dialog_sale_desc: "Physical shop sale — decrements stock and writes STORE_SALE.",
    dialog_plus_title: "Add stock", dialog_plus_cta: "Add stock",
    dialog_plus_desc: "Increment variant (shipment, restock). Cannot go negative.",
    dialog_minus_title: "Remove stock", dialog_minus_cta: "Remove stock",
    dialog_minus_desc: "Decrement variant (damage, write-off). Cannot go negative.",
    dialog_set_title: "Set exact quantity", dialog_set_cta: "Set exact",
    dialog_set_desc: "Overwrite to the entered number. Recorded as STOCK_ADJUSTMENT with the delta.",
    new_qty: "New quantity", amount: "Amount", reason_label: "Reason",
    reason_stock_added: "STOCK_ADDED — restock / shipment",
    reason_return_full: "RETURN — customer return re-shelved",
    reason_other_full: "OTHER",
    reason_stock_removed: "STOCK_REMOVED — damage / write-off",
    note_ledger: "Note (optional, appended to ledger)",
    note_placeholder: "Reason / reference / recount notes…",
    preview_after: "Preview after change:",
    cannot_negative: "Stock cannot go negative.",
    set_confirm: "Set variant \"{size}\" of \"{slug}\" to EXACTLY {qty}? Current: {current}. This is logged in the audit ledger.",
    err_choose_first: "Choose a product & variant first",
    err_enter_qty: "Enter a quantity", err_negative: "Quantity cannot be negative",
    err_sale_zero: "Sale quantity must be > 0", err_only_x: "Only {n} in stock",
    err_positive: "Amount must be > 0", err_only_x_neg: "Only {n} in stock (cannot go negative)",
    toast_sale: "Sale recorded — {n} unit(s) removed",
    toast_added: "+{n} added", toast_removed: "−{n} removed",
    toast_set: "Stock set to {n}",
    default_variant: "—",
  },
  customers: {
    col_email: "Email", col_name: "Name", col_orders: "Orders",
    col_total: "Total CHF", col_last_order: "Last order",
  },
  reviews: {
    empty: "No reviews.", approve: "Approve", hide: "Hide", verified: "Verified",
    st_approved: "approved", st_pending: "pending", st_hidden: "hidden",
  },
  questions: {
    empty: "No questions.", approve: "Approve", answer: "Answer",
    answer_prompt: "Answer:",
  },
  discounts: {
    code_placeholder: "CODE", type_percent: "percent %", type_fixed: "fixed CHF",
    value_placeholder: "Value", min_order_placeholder: "Min order CHF",
    max_uses_placeholder: "Max uses (0=∞)",
    first_order_only: "First order only", create: "Create",
    col_code: "Code", col_type: "Type", col_value: "Value",
    col_min_order: "Min order", col_uses: "Uses", col_active: "Active",
    col_actions: "Actions", toggle: "Toggle", del: "Del",
    delete_confirm: "Delete?", created: "Created",
  },
  consultations: {
    empty: "No consultations.", mark_contacted: "Mark contacted", close: "Close",
    preferred: "Preferred:",
  },
  basra: {
    subtitle: "Latest {n} Basra conversations (max. 20, newest first).",
    empty: "No conversations.",
    customer_messages: "customer messages", total_messages: "total",
    no_customer_message: "(no customer message)",
    close_conversation: "Close conversation", failed_to_load: "Failed to load conversation",
    booking_label: "Booking", product_label: "Product", cart_label: "Cart",
    cart_set: "set_qty", cart_remove: "remove",
  },
  returns: {
    subtitle: "All return requests (newest first).", empty: "No returns.",
    product_issue: "Product issue", customer_reason: "Customer reason",
    free_return: "Free return", customer_paid: "Customer-paid",
    reason_label: "Reason:", requested: "Requested:",
    pledge_accepted: "Pledge accepted:",
    order: "Order", order_status: "Order status:", total: "Total",
    return_request: "Return request",
    receipt_inspection: "Receipt & inspection", received: "Received",
    cond_unopened: "unopened", cond_opened: "opened", cond_damaged: "damaged", cond_other: "other",
    mark_received: "Mark as received", marked_received: "Marked as received",
    inspection_notes: "Inspection notes",
    inspection_notes_placeholder: "Condition on arrival, packaging, seals…",
    refund_override: "Refund override (CHF)", refund_override_auto: "auto",
    inspect_fail: "Fail", inspect_pass_refund: "Pass · Refund",
    inspection_result: "Inspection:",
    refund: "Refund", refund_status: "Refund status",
    internal_notes: "Internal notes (private)",
    internal_notes_placeholder: "Only visible to admins…",
    resolution_title: "Resolution (only used when approving)",
    free_return_desc: "— salon covers shipping · issues a RMA code",
    customer_paid_desc: "— customer covers shipping",
    send_email: "Send decision e-mail to the customer",
    save_notes: "Save notes", under_review: "Under review",
    reject: "Reject", approve: "Approve",
    updated: "Return updated", failed_to_load: "Failed to load return",
    inspection_passed_toast: "Inspection passed — refund initiated",
    inspection_failed_toast: "Inspection failed",
    refund_failed_toast: "Refund could not be processed — see details",
    rma: "RMA",
  },
};

const de = {
  common: {
    admin: "Admin", back_to_site: "Zur Website", logout: "Abmelden",
    cancel: "Abbrechen", save: "Speichern", saving: "Speichere…", delete: "Löschen",
    edit: "Bearbeiten", close: "Schliessen", add: "Hinzufügen", remove: "Entfernen",
    apply: "Anwenden", refresh: "Aktualisieren", search: "Suchen…", actions: "Aktionen",
    status: "Status", active: "Aktiv", hidden: "Versteckt", published: "Veröffentlicht",
    archived: "Archiviert", loading: "Lade…", no_data: "—", empty: "—",
    yes: "Ja", no: "Nein", confirm: "Bestätigen", name: "Name", email: "E-Mail",
    phone: "Telefon", price: "Preis", total: "Gesamt", quantity: "Menge",
    note: "Notiz", optional: "optional", required: "Erforderlich", all: "Alle",
    approve: "Freigeben", hide: "Verstecken", show: "Anzeigen", toggle: "Umschalten",
    duplicate: "Duplizieren", copy: "Kopie", updated: "Aktualisiert", created: "Erstellt",
    deleted: "Gelöscht", error: "Fehler", failed: "Fehlgeschlagen",
    failed_to_load: "Laden fehlgeschlagen", failed_to_update: "Aktualisierung fehlgeschlagen",
    save_failed: "Speichern fehlgeschlagen", delete_failed: "Löschen fehlgeschlagen",
    load_failed: "Laden fehlgeschlagen", operation_failed: "Vorgang fehlgeschlagen",
    del: "Löschen", details: "Details", back: "Zurück", new: "Neu",
  },
  login: {
    heading: "Coiffeur Tattoo 01&Co.", eyebrow: "Admin",
    email_placeholder: "E-Mail", password_placeholder: "Passwort",
    submit: "Anmelden", loading: "…", not_admin: "Kein Admin-Konto",
    generic_error: "Fehler",
  },
  tabs: {
    analytics: "Analyse", orders: "Bestellungen", appointments: "Termine",
    products: "Produkte", categories: "Kategorien", inventory: "Lager",
    customers: "Kunden", reviews: "Bewertungen", questions: "F&A",
    discounts: "Rabatte", consultations: "Beratungen", basra: "Basra",
    returns: "Retouren",
  },
  analytics: {
    revenue_chf: "Umsatz CHF", orders: "Bestellungen", avg_order: "Ø Bestellung",
    customers: "Kunden", page_views: "Seitenaufrufe", add_to_cart: "In den Warenkorb",
    checkout_starts: "Kassenstarts", total_events: "Ereignisse gesamt",
    revenue_by_country: "Umsatz nach Land", revenue_by_currency: "Umsatz nach Währung",
  },
  orders: {
    order: "Bestellung", email: "E-Mail", country: "Land", status: "Status",
    total_chf: "Gesamt CHF", return: "Retoure", actions: "Aktionen",
    pledge: "Zusage", tracking_title: "Sendungsverfolgung",
    tracking_prompt_carrier: "Versanddienst (z.B. Swiss Post):",
    tracking_prompt_number: "Tracking-Nr.:", tracking_prompt_url: "Tracking-URL:",
    refund: "Erstatten", refund_confirm: "Diese Bestellung erstatten?",
    refund_success: "Erstattet", refund_error: "Erstattung fehlgeschlagen",
    empty: "Noch keine Bestellungen.",
  },
  appointments: {
    title: "Termine", legend: "🟢 bestätigt · 🔴 storniert · 🟠 nicht erschienen",
    block_slot: "Zeit sperren",
    blocked_times: "Gesperrte Zeiten", when: "Zeitpunkt", customer: "Zubon",
    service: "Service", price: "Preis", empty: "Keine Termine",
    extend_confirm: "Termin um 30 Minuten verlängern und Zubon per E-Mail benachrichtigen?",
    extended: "Termin verlängert",
    noshow_confirm: "Als \"No-Show\" markieren und 50% Gebühr (CHF {fee}) berechnen?",
    marked_noshow: "Als No-Show markiert", extend_30: "+30 Min", no_show: "No-Show",
    block_prompt_start: "Startzeit sperren (ISO, z.B. 2026-09-14T14:15:00+01:00):",
    block_prompt_note: "Notiz:", block_default_note: "Belegt",
    block_success: "Zeit gesperrt",
    unblock_confirm: "Sperrung aufheben?", unblocked: "Freigegeben",
    error: "Fehler",
  },
  products: {
    title: "Produkte", search: "Suchen…", new: "Neues Produkt",
    col_slug: "Slug", col_name_de: "Name (DE)", col_category: "Kategorie",
    col_price: "Preis", col_variants: "Varianten · Bestand", col_status: "Status",
    col_actions: "Aktionen", needs_setup: "Setup nötig", no_results: "Keine Produkte gefunden.",
    duplicate: "Duplizieren", edit: "Bearbeiten", delete: "Löschen",
    hide: "Verstecken", show: "Anzeigen",
    duplicate_confirm: "\"{slug}\" duplizieren? Die Kopie ist versteckt und ohne Bestand.",
    duplicated: "Dupliziert → {slug}", duplicate_failed: "Duplizieren fehlgeschlagen",
    delete_confirm: "\"{slug}\" löschen? Produkte mit Bestellhistorie werden archiviert, nicht gelöscht.",
    archived_msg: "Archiviert (Bestellhistorie vorhanden)", deleted: "Gelöscht",
    updated: "Produkt aktualisiert",
    created_reminder: "Produkt erstellt — bitte Bestand im Lager-Tab setzen",
    slug_required: "Slug ist erforderlich",
    section_info: "Produktinformationen", section_prices: "Preise",
    section_translations: "Übersetzungen (Name + Beschreibung)",
    section_variants: "Varianten / Grössen",
    section_images: "Bilder (vom Computer hochladen)",
    section_seo: "SEO (pro Sprache)", section_publishing: "Veröffentlichung",
    field_slug: "Slug (URL-Kennung)", field_category: "Kategorie",
    field_sku: "SKU", field_weight: "Gewicht (g)",
    field_regular: "Regulärer Preis (CHF)", field_sale: "Aktionspreis (CHF, optional)",
    field_discount: "Rabatt % (auto)", discount_none: "Kein Rabatt",
    field_sale_starts: "Rabatt beginnt (optional)", field_sale_ends: "Rabatt endet (optional)",
    field_name: "Name", field_desc: "Beschreibung (Markdown unterstützt)",
    desc_placeholder: "**Fett**, *kursiv*, - Listen, Links — alles wird auf der Produktseite gerendert.",
    add_size: "+ Grösse hinzufügen", add_size_prompt: "Varianten- / Grössen-Label (z.B. 100ml)",
    stock_hint: "Der Bestand pro Variante wird über die \"Bestand\"-Buttons in der Produktliste verwaltet — jede Änderung wird im Lager-Ledger festgehalten.",
    field_seo_title: "SEO-Titel", field_seo_desc: "SEO-Beschreibung",
    checkbox_published: "Veröffentlicht (im Shop sichtbar)", checkbox_featured: "Hervorgehoben",
    checkbox_bestseller: "Bestseller", checkbox_new: "Neu",
    eyebrow_new: "Neues Produkt", eyebrow_editing: "Bearbeiten", untitled: "Ohne Titel",
    btn_create: "Produkt erstellen", btn_save: "Änderungen speichern",
    new_category_btn: "+ Neu", new_category_prompt: "Neuer Kategoriename (Deutsch):",
    category_created: "Kategorie erstellt", category_create_failed: "Erstellen fehlgeschlagen",
    stock_dialog_title: "Bestand · {name}", stock_variant: "Variante",
    stock_current: "Aktuell", stock_mode_sale: "Ladenverkauf", stock_mode_plus: "Zugang +",
    stock_mode_minus: "Abgang −", stock_mode_set: "Genau setzen",
    stock_qty: "Menge", stock_note_placeholder: "Lieferung eingetroffen, Schaden, Zählung…",
    stock_note: "Notiz (optional)", stock_apply: "Anwenden",
    inventory_updated: "Bestand aktualisiert",
  },
  categories: {
    title: "Kategorien", new: "Neue Kategorie",
    col_image: "Bild", col_slug: "Slug", col_name_de: "Name (DE)",
    col_position: "Position", col_status: "Status", col_actions: "Aktionen",
    empty: "Noch keine Kategorien — erstellen Sie eine.",
    delete_confirm: "\"{slug}\" löschen? (blockiert, wenn Produkte noch zugeordnet sind)",
    updated: "Aktualisiert", created: "Erstellt", deleted: "Gelöscht",
    form_new: "Neue Kategorie", form_edit: "Kategorie bearbeiten",
    field_slug: "Slug (URL)", field_name_placeholder: "Name",
    field_desc_placeholder: "Beschreibung (optional)", field_image: "Kategoriebild",
    field_active: "Aktiv", field_position: "Position",
    slug_placeholder: "natuerliche-seifen",
  },
  inventory: {
    title: "Lager",
    subtitle: "Einheitlich für Online + Ladengeschäft. Jede Änderung wird im Audit-Ledger protokolliert.",
    refresh: "Aktualisieren",
    products_count: "Produkte ({n})",
    alerts_badge: "Warnungen", alerts_show_all: "Alle Produkte anzeigen",
    alerts_show_only: "Nur niedrig/kritisch/leer anzeigen",
    empty_all_ok: "Aktuell keine Varianten im Warnzustand 🎉", empty_none: "Keine Produkte",
    setup: "Setup",
    choose_prompt: "Wählen Sie links ein Produkt und eine Variante",
    selected: "Ausgewählt", variant: "Variante", current_stock: "Aktueller Bestand",
    action_store_sale: "Ladenverkauf", hint_store_sale: "Physischer Laden",
    action_add_stock: "Bestand hinzufügen", hint_add_stock: "Nachschub, Lieferung",
    action_remove_stock: "Bestand entfernen", hint_remove_stock: "Schaden, Abschreibung",
    action_set_exact: "Genau setzen", hint_set_exact: "Zählung, Korrektur",
    visibility_hint: "Reale Bestandszahlen sind nur im Admin sichtbar. Kunden sehen einen Zustand (verfügbar / niedrig / leer) — nie die exakte Menge über 4.",
    movement_history: "Bewegungsverlauf", filter: "Filter:", all_products: "Alle Produkte",
    all_reasons: "Alle Gründe",
    reason_online: "Online-Bestellung", reason_store: "Ladenverkauf", reason_added: "Bestand hinzugefügt",
    reason_removed: "Bestand entfernt", reason_adjust: "Anpassung", reason_return: "Retoure",
    reason_other: "Anderes",
    col_when: "Wann", col_product: "Produkt", col_variant: "Variante",
    col_before: "Vorher", col_delta: "Δ", col_after: "Nachher",
    col_reason: "Grund", col_source: "Quelle", col_ref_admin: "Ref / Admin",
    col_note: "Notiz", no_movements: "Noch keine Bewegungen erfasst.",
    deleted_product: "gelöscht",
    pill_online: "Online", pill_store: "Laden", pill_added: "Zugang",
    pill_removed: "Abgang", pill_adjust: "Anpassung", pill_return: "Retoure",
    pill_other: "Anderes",
    dialog_sale_title: "Ladenverkauf erfassen", dialog_sale_cta: "Verkauf erfassen",
    dialog_sale_desc: "Ladenverkauf — reduziert Bestand und schreibt STORE_SALE.",
    dialog_plus_title: "Bestand hinzufügen", dialog_plus_cta: "Bestand hinzufügen",
    dialog_plus_desc: "Variante erhöhen (Lieferung, Nachschub). Kann nicht negativ werden.",
    dialog_minus_title: "Bestand entfernen", dialog_minus_cta: "Bestand entfernen",
    dialog_minus_desc: "Variante verringern (Schaden, Abschreibung). Kann nicht negativ werden.",
    dialog_set_title: "Exakte Menge setzen", dialog_set_cta: "Genau setzen",
    dialog_set_desc: "Auf eingegebene Zahl überschreiben. Als STOCK_ADJUSTMENT mit Delta erfasst.",
    new_qty: "Neue Menge", amount: "Menge", reason_label: "Grund",
    reason_stock_added: "STOCK_ADDED — Nachschub / Lieferung",
    reason_return_full: "RETURN — Kundenretoure wieder eingeräumt",
    reason_other_full: "OTHER",
    reason_stock_removed: "STOCK_REMOVED — Schaden / Abschreibung",
    note_ledger: "Notiz (optional, ins Ledger übernommen)",
    note_placeholder: "Grund / Referenz / Zählungshinweise…",
    preview_after: "Vorschau nach Änderung:",
    cannot_negative: "Bestand kann nicht negativ werden.",
    set_confirm: "Variante \"{size}\" von \"{slug}\" auf GENAU {qty} setzen? Aktuell: {current}. Dies wird im Audit-Ledger protokolliert.",
    err_choose_first: "Wählen Sie zuerst Produkt und Variante",
    err_enter_qty: "Menge eingeben", err_negative: "Menge kann nicht negativ sein",
    err_sale_zero: "Verkaufsmenge muss > 0 sein", err_only_x: "Nur {n} auf Lager",
    err_positive: "Menge muss > 0 sein", err_only_x_neg: "Nur {n} auf Lager (kann nicht negativ werden)",
    toast_sale: "Verkauf erfasst — {n} Einheit(en) entfernt",
    toast_added: "+{n} hinzugefügt", toast_removed: "−{n} entfernt",
    toast_set: "Bestand auf {n} gesetzt",
    default_variant: "—",
  },
  customers: {
    col_email: "E-Mail", col_name: "Name", col_orders: "Bestellungen",
    col_total: "Gesamt CHF", col_last_order: "Letzte Bestellung",
  },
  reviews: {
    empty: "Keine Bewertungen.", approve: "Freigeben", hide: "Verstecken", verified: "Verifiziert",
    st_approved: "freigegeben", st_pending: "ausstehend", st_hidden: "versteckt",
  },
  questions: {
    empty: "Keine Fragen.", approve: "Freigeben", answer: "Antworten",
    answer_prompt: "Antwort:",
  },
  discounts: {
    code_placeholder: "CODE", type_percent: "Prozent %", type_fixed: "Fest CHF",
    value_placeholder: "Wert", min_order_placeholder: "Mindestbestellwert CHF",
    max_uses_placeholder: "Max. Nutzungen (0=∞)",
    first_order_only: "Nur Erstbestellung", create: "Erstellen",
    col_code: "Code", col_type: "Typ", col_value: "Wert",
    col_min_order: "Mindestwert", col_uses: "Nutzungen", col_active: "Aktiv",
    col_actions: "Aktionen", toggle: "Umschalten", del: "Löschen",
    delete_confirm: "Löschen?", created: "Erstellt",
  },
  consultations: {
    empty: "Keine Beratungen.", mark_contacted: "Kontaktiert markieren", close: "Schliessen",
    preferred: "Bevorzugt:",
  },
  basra: {
    subtitle: "Letzte {n} Basra-Konversationen (max. 20, neueste zuerst).",
    empty: "Keine Konversationen.",
    customer_messages: "Kunden-Nachrichten", total_messages: "gesamt",
    no_customer_message: "(keine Kundennachricht)",
    close_conversation: "Konversation schliessen",
    failed_to_load: "Konversation konnte nicht geladen werden",
    booking_label: "Buchung", product_label: "Produkt", cart_label: "Warenkorb",
    cart_set: "set_qty", cart_remove: "entfernen",
  },
  returns: {
    subtitle: "Alle Retouren-Anfragen (neueste zuerst).", empty: "Keine Retouren.",
    product_issue: "Produktproblem", customer_reason: "Kundengrund",
    free_return: "Kostenlose Retoure", customer_paid: "Kundenbezahlt",
    reason_label: "Grund:", requested: "Angefragt:",
    pledge_accepted: "Zusage akzeptiert:",
    order: "Bestellung", order_status: "Bestellstatus:", total: "Gesamt",
    return_request: "Retoure-Anfrage",
    receipt_inspection: "Eingang & Prüfung", received: "Erhalten",
    cond_unopened: "ungeöffnet", cond_opened: "geöffnet", cond_damaged: "beschädigt", cond_other: "andere",
    mark_received: "Als erhalten markieren", marked_received: "Als erhalten markiert",
    inspection_notes: "Prüfungsnotizen",
    inspection_notes_placeholder: "Zustand bei Ankunft, Verpackung, Siegel…",
    refund_override: "Erstattung überschreiben (CHF)", refund_override_auto: "auto",
    inspect_fail: "Fehlgeschlagen", inspect_pass_refund: "Bestanden · Erstattung",
    inspection_result: "Prüfung:",
    refund: "Erstattung", refund_status: "Erstattungsstatus",
    internal_notes: "Interne Notizen (privat)",
    internal_notes_placeholder: "Nur für Admins sichtbar…",
    resolution_title: "Lösung (nur bei Genehmigung verwendet)",
    free_return_desc: "— Salon übernimmt Versand · vergibt RMA-Code",
    customer_paid_desc: "— Kunde übernimmt Versand",
    send_email: "Entscheidungs-E-Mail an den Kunden senden",
    save_notes: "Notizen speichern", under_review: "In Prüfung",
    reject: "Ablehnen", approve: "Genehmigen",
    updated: "Retoure aktualisiert", failed_to_load: "Retoure konnte nicht geladen werden",
    inspection_passed_toast: "Prüfung bestanden — Erstattung eingeleitet",
    inspection_failed_toast: "Prüfung fehlgeschlagen",
    refund_failed_toast: "Erstattung konnte nicht verarbeitet werden — Details prüfen",
    rma: "RMA",
  },
};

const ar = {
  common: {
    admin: "الإدارة", back_to_site: "العودة إلى الموقع", logout: "تسجيل الخروج",
    cancel: "إلغاء", save: "حفظ", saving: "جارٍ الحفظ…", delete: "حذف",
    edit: "تعديل", close: "إغلاق", add: "إضافة", remove: "إزالة",
    apply: "تطبيق", refresh: "تحديث", search: "بحث…", actions: "الإجراءات",
    status: "الحالة", active: "مفعل", hidden: "مخفي", published: "منشور",
    archived: "مؤرشف", loading: "جارٍ التحميل…", no_data: "—", empty: "—",
    yes: "نعم", no: "لا", confirm: "تأكيد", name: "الاسم", email: "البريد الإلكتروني",
    phone: "الهاتف", price: "السعر", total: "الإجمالي", quantity: "الكمية",
    note: "ملاحظة", optional: "اختياري", required: "مطلوب", all: "الكل",
    approve: "الموافقة", hide: "إخفاء", show: "إظهار", toggle: "تبديل",
    duplicate: "تكرار", copy: "نسخة", updated: "تم التحديث", created: "تم الإنشاء",
    deleted: "تم الحذف", error: "خطأ", failed: "فشل",
    failed_to_load: "فشل التحميل", failed_to_update: "فشل التحديث",
    save_failed: "فشل الحفظ", delete_failed: "فشل الحذف",
    load_failed: "فشل التحميل", operation_failed: "فشلت العملية",
    del: "حذف", details: "التفاصيل", back: "رجوع", new: "جديد",
  },
  login: {
    heading: "Coiffeur Tattoo 01&Co.", eyebrow: "الإدارة",
    email_placeholder: "البريد الإلكتروني", password_placeholder: "كلمة المرور",
    submit: "تسجيل الدخول", loading: "…", not_admin: "ليس حساب إداري",
    generic_error: "خطأ",
  },
  tabs: {
    analytics: "التحليلات", orders: "الطلبات", appointments: "المواعيد",
    products: "المنتجات", categories: "الفئات", inventory: "المخزون",
    customers: "العملاء", reviews: "المراجعات", questions: "أسئلة وأجوبة",
    discounts: "الخصومات", consultations: "الاستشارات", basra: "بصرة",
    returns: "المرتجعات",
  },
  analytics: {
    revenue_chf: "الإيرادات CHF", orders: "الطلبات", avg_order: "متوسط الطلب",
    customers: "العملاء", page_views: "مشاهدات الصفحة", add_to_cart: "الإضافة إلى السلة",
    checkout_starts: "بدء الدفع", total_events: "إجمالي الأحداث",
    revenue_by_country: "الإيرادات حسب البلد", revenue_by_currency: "الإيرادات حسب العملة",
  },
  orders: {
    order: "الطلب", email: "البريد", country: "البلد", status: "الحالة",
    total_chf: "الإجمالي CHF", return: "الإرجاع", actions: "الإجراءات",
    pledge: "تعهد", tracking_title: "تعيين التتبع",
    tracking_prompt_carrier: "شركة الشحن (مثل Swiss Post):",
    tracking_prompt_number: "رقم التتبع:", tracking_prompt_url: "رابط التتبع:",
    refund: "استرداد", refund_confirm: "هل تريد استرداد هذا الطلب؟",
    refund_success: "تم الاسترداد", refund_error: "فشل الاسترداد",
    empty: "لا توجد طلبات بعد.",
  },
  appointments: {
    title: "المواعيد", legend: "🟢 مؤكد · 🔴 ملغى · 🟠 لم يحضر",
    block_slot: "حجب وقت",
    blocked_times: "الأوقات المحجوبة", when: "الوقت", customer: "العميل",
    service: "الخدمة", price: "السعر", empty: "لا توجد مواعيد",
    extend_confirm: "تمديد الموعد 30 دقيقة وإشعار العميل بالبريد الإلكتروني؟",
    extended: "تم تمديد الموعد",
    noshow_confirm: "وضع علامة \"لم يحضر\" وفرض رسم 50% (CHF {fee})؟",
    marked_noshow: "تم وضع علامة لم يحضر", extend_30: "+30 دقيقة", no_show: "لم يحضر",
    block_prompt_start: "وقت البدء المراد حجبه (ISO، مثل 2026-09-14T14:15:00+01:00):",
    block_prompt_note: "ملاحظة:", block_default_note: "محجوز",
    block_success: "تم حجب الوقت",
    unblock_confirm: "إلغاء الحجب؟", unblocked: "تم الإلغاء",
    error: "خطأ",
  },
  products: {
    title: "المنتجات", search: "بحث…", new: "منتج جديد",
    col_slug: "Slug", col_name_de: "الاسم (DE)", col_category: "الفئة",
    col_price: "السعر", col_variants: "المتغيرات · المخزون", col_status: "الحالة",
    col_actions: "الإجراءات", needs_setup: "بحاجة إلى إعداد", no_results: "لا توجد منتجات مطابقة.",
    duplicate: "تكرار", edit: "تعديل", delete: "حذف",
    hide: "إخفاء", show: "إظهار",
    duplicate_confirm: "تكرار \"{slug}\"؟ ستكون النسخة مخفية وبدون مخزون.",
    duplicated: "تم التكرار → {slug}", duplicate_failed: "فشل التكرار",
    delete_confirm: "حذف \"{slug}\"؟ المنتجات المرتبطة بطلبات ستُؤرشف وليس تُحذف.",
    archived_msg: "تمت الأرشفة (يوجد تاريخ طلبات)", deleted: "تم الحذف",
    updated: "تم تحديث المنتج",
    created_reminder: "تم إنشاء المنتج — تذكّر ضبط المخزون في تبويب المخزون",
    slug_required: "Slug مطلوب",
    section_info: "معلومات المنتج", section_prices: "الأسعار",
    section_translations: "الترجمات (الاسم + الوصف)",
    section_variants: "المتغيرات / المقاسات",
    section_images: "الصور (رفع من جهازك)",
    section_seo: "SEO (لكل لغة)", section_publishing: "النشر",
    field_slug: "Slug (معرّف الرابط)", field_category: "الفئة",
    field_sku: "SKU", field_weight: "الوزن (غ)",
    field_regular: "السعر العادي (CHF)", field_sale: "سعر التخفيض (CHF، اختياري)",
    field_discount: "نسبة الخصم (تلقائي)", discount_none: "لا يوجد خصم",
    field_sale_starts: "بدء الخصم (اختياري)", field_sale_ends: "نهاية الخصم (اختياري)",
    field_name: "الاسم", field_desc: "الوصف (يدعم Markdown)",
    desc_placeholder: "**غامق**, *مائل*, - قوائم، روابط — كل شيء يظهر في صفحة المنتج.",
    add_size: "+ إضافة مقاس", add_size_prompt: "تسمية المتغير / المقاس (مثل 100ml)",
    stock_hint: "يُدار المخزون لكل متغير عبر أزرار \"المخزون\" في قائمة المنتجات — كل تغيير يُسجّل في سجل المخزون.",
    field_seo_title: "عنوان SEO", field_seo_desc: "وصف SEO",
    checkbox_published: "منشور (ظاهر في المتجر)", checkbox_featured: "مميز",
    checkbox_bestseller: "الأكثر مبيعاً", checkbox_new: "جديد",
    eyebrow_new: "منتج جديد", eyebrow_editing: "تعديل", untitled: "بدون عنوان",
    btn_create: "إنشاء المنتج", btn_save: "حفظ التغييرات",
    new_category_btn: "+ جديد", new_category_prompt: "اسم فئة جديدة (بالألمانية):",
    category_created: "تم إنشاء الفئة", category_create_failed: "فشل الإنشاء",
    stock_dialog_title: "المخزون · {name}", stock_variant: "المتغير",
    stock_current: "الحالي", stock_mode_sale: "بيع بالمتجر", stock_mode_plus: "إضافة +",
    stock_mode_minus: "إزالة −", stock_mode_set: "تعيين تماماً",
    stock_qty: "الكمية", stock_note_placeholder: "وصلت شحنة، تلف، جرد…",
    stock_note: "ملاحظة (اختياري)", stock_apply: "تطبيق",
    inventory_updated: "تم تحديث المخزون",
  },
  categories: {
    title: "الفئات", new: "فئة جديدة",
    col_image: "الصورة", col_slug: "Slug", col_name_de: "الاسم (DE)",
    col_position: "الترتيب", col_status: "الحالة", col_actions: "الإجراءات",
    empty: "لا توجد فئات بعد — أنشئ واحدة.",
    delete_confirm: "حذف \"{slug}\"؟ (محظور إذا كانت هناك منتجات تستخدمها)",
    updated: "تم التحديث", created: "تم الإنشاء", deleted: "تم الحذف",
    form_new: "فئة جديدة", form_edit: "تعديل الفئة",
    field_slug: "Slug (URL)", field_name_placeholder: "الاسم",
    field_desc_placeholder: "الوصف (اختياري)", field_image: "صورة الفئة",
    field_active: "مفعل", field_position: "الترتيب",
    slug_placeholder: "natural-soaps",
  },
  inventory: {
    title: "المخزون",
    subtitle: "موحّد بين الإنترنت والمتجر. كل تغيير يُسجّل في سجل التدقيق.",
    refresh: "تحديث",
    products_count: "المنتجات ({n})",
    alerts_badge: "تنبيهات", alerts_show_all: "عرض كل المنتجات",
    alerts_show_only: "عرض المتغيرات المنخفضة/الحرجة/النافدة فقط",
    empty_all_ok: "لا توجد متغيرات في حالة تنبيه حالياً 🎉", empty_none: "لا توجد منتجات",
    setup: "إعداد",
    choose_prompt: "اختر منتجاً ومتغيراً من اليسار",
    selected: "المختار", variant: "المتغير", current_stock: "المخزون الحالي",
    action_store_sale: "بيع بالمتجر", hint_store_sale: "المتجر الفعلي",
    action_add_stock: "إضافة مخزون", hint_add_stock: "إعادة تعبئة، شحنة",
    action_remove_stock: "إزالة مخزون", hint_remove_stock: "تلف، إتلاف",
    action_set_exact: "تعيين تماماً", hint_set_exact: "جرد، تصحيح",
    visibility_hint: "أرقام المخزون الحقيقية تظهر فقط في لوحة الإدارة. يرى العملاء الحالة (متوفر / منخفض / نافد) — أبداً العدد الفعلي فوق 4.",
    movement_history: "سجل الحركات", filter: "تصفية:", all_products: "كل المنتجات",
    all_reasons: "كل الأسباب",
    reason_online: "طلب إلكتروني", reason_store: "بيع بالمتجر", reason_added: "تمت إضافة مخزون",
    reason_removed: "تمت إزالة مخزون", reason_adjust: "تعديل", reason_return: "إرجاع",
    reason_other: "أخرى",
    col_when: "الوقت", col_product: "المنتج", col_variant: "المتغير",
    col_before: "قبل", col_delta: "Δ", col_after: "بعد",
    col_reason: "السبب", col_source: "المصدر", col_ref_admin: "مرجع / المسؤول",
    col_note: "ملاحظة", no_movements: "لم تُسجَّل حركات بعد.",
    deleted_product: "محذوف",
    pill_online: "إلكتروني", pill_store: "متجر", pill_added: "مُضاف",
    pill_removed: "مُزال", pill_adjust: "تعديل", pill_return: "إرجاع",
    pill_other: "أخرى",
    dialog_sale_title: "تسجيل بيع بالمتجر", dialog_sale_cta: "تسجيل البيع",
    dialog_sale_desc: "بيع من المتجر الفعلي — يُنقص المخزون ويكتب STORE_SALE.",
    dialog_plus_title: "إضافة مخزون", dialog_plus_cta: "إضافة مخزون",
    dialog_plus_desc: "زيادة المتغير (شحنة، إعادة تعبئة). لا يمكن أن يصبح سالباً.",
    dialog_minus_title: "إزالة مخزون", dialog_minus_cta: "إزالة مخزون",
    dialog_minus_desc: "تقليل المتغير (تلف، إتلاف). لا يمكن أن يصبح سالباً.",
    dialog_set_title: "تعيين كمية تماماً", dialog_set_cta: "تعيين تماماً",
    dialog_set_desc: "الكتابة فوق الرقم المُدخل. يُسجَّل كـ STOCK_ADJUSTMENT مع الفارق.",
    new_qty: "الكمية الجديدة", amount: "الكمية", reason_label: "السبب",
    reason_stock_added: "STOCK_ADDED — إعادة تعبئة / شحنة",
    reason_return_full: "RETURN — إعادة عميل تمت إعادتها للرف",
    reason_other_full: "OTHER",
    reason_stock_removed: "STOCK_REMOVED — تلف / إتلاف",
    note_ledger: "ملاحظة (اختياري، تُضاف إلى السجل)",
    note_placeholder: "السبب / المرجع / ملاحظات الجرد…",
    preview_after: "معاينة بعد التغيير:",
    cannot_negative: "لا يمكن أن يصبح المخزون سالباً.",
    set_confirm: "ضبط المتغير \"{size}\" لـ \"{slug}\" ليكون تماماً {qty}؟ الحالي: {current}. يُسجَّل هذا في سجل التدقيق.",
    err_choose_first: "اختر منتجاً ومتغيراً أولاً",
    err_enter_qty: "أدخل كمية", err_negative: "لا يمكن أن تكون الكمية سالبة",
    err_sale_zero: "يجب أن تكون كمية البيع > 0", err_only_x: "{n} فقط في المخزون",
    err_positive: "يجب أن تكون الكمية > 0", err_only_x_neg: "{n} فقط في المخزون (لا يمكن أن يصبح سالباً)",
    toast_sale: "تم تسجيل البيع — إزالة {n} وحدة",
    toast_added: "+{n} أضيفت", toast_removed: "−{n} أُزيلت",
    toast_set: "تم تعيين المخزون على {n}",
    default_variant: "—",
  },
  customers: {
    col_email: "البريد", col_name: "الاسم", col_orders: "الطلبات",
    col_total: "الإجمالي CHF", col_last_order: "آخر طلب",
  },
  reviews: {
    empty: "لا توجد مراجعات.", approve: "الموافقة", hide: "إخفاء", verified: "موثّق",
    st_approved: "موافَق عليها", st_pending: "قيد الانتظار", st_hidden: "مخفية",
  },
  questions: {
    empty: "لا توجد أسئلة.", approve: "الموافقة", answer: "الرد",
    answer_prompt: "الرد:",
  },
  discounts: {
    code_placeholder: "الرمز", type_percent: "نسبة %", type_fixed: "ثابت CHF",
    value_placeholder: "القيمة", min_order_placeholder: "الحد الأدنى للطلب CHF",
    max_uses_placeholder: "أقصى استخدام (0=∞)",
    first_order_only: "أول طلب فقط", create: "إنشاء",
    col_code: "الرمز", col_type: "النوع", col_value: "القيمة",
    col_min_order: "الحد الأدنى", col_uses: "الاستخدامات", col_active: "مفعل",
    col_actions: "الإجراءات", toggle: "تبديل", del: "حذف",
    delete_confirm: "حذف؟", created: "تم الإنشاء",
  },
  consultations: {
    empty: "لا توجد استشارات.", mark_contacted: "وضع علامة تم التواصل", close: "إغلاق",
    preferred: "المفضّل:",
  },
  basra: {
    subtitle: "آخر {n} محادثات مع بصرة (بحد أقصى 20، الأحدث أولاً).",
    empty: "لا توجد محادثات.",
    customer_messages: "رسائل العميل", total_messages: "الإجمالي",
    no_customer_message: "(لا توجد رسالة من العميل)",
    close_conversation: "إغلاق المحادثة",
    failed_to_load: "فشل تحميل المحادثة",
    booking_label: "حجز", product_label: "منتج", cart_label: "سلة",
    cart_set: "set_qty", cart_remove: "إزالة",
  },
  returns: {
    subtitle: "كل طلبات الإرجاع (الأحدث أولاً).", empty: "لا توجد مرتجعات.",
    product_issue: "مشكلة في المنتج", customer_reason: "سبب العميل",
    free_return: "إرجاع مجاني", customer_paid: "مدفوع من العميل",
    reason_label: "السبب:", requested: "طُلب في:",
    pledge_accepted: "تم قبول التعهد:",
    order: "الطلب", order_status: "حالة الطلب:", total: "الإجمالي",
    return_request: "طلب الإرجاع",
    receipt_inspection: "الاستلام والفحص", received: "تم الاستلام",
    cond_unopened: "غير مفتوح", cond_opened: "مفتوح", cond_damaged: "تالف", cond_other: "أخرى",
    mark_received: "وضع علامة تم الاستلام", marked_received: "تم وضع علامة الاستلام",
    inspection_notes: "ملاحظات الفحص",
    inspection_notes_placeholder: "الحالة عند الوصول، التغليف، الأختام…",
    refund_override: "تجاوز الاسترداد (CHF)", refund_override_auto: "تلقائي",
    inspect_fail: "فاشل", inspect_pass_refund: "ناجح · استرداد",
    inspection_result: "الفحص:",
    refund: "الاسترداد", refund_status: "حالة الاسترداد",
    internal_notes: "ملاحظات داخلية (خاصة)",
    internal_notes_placeholder: "مرئية للمسؤولين فقط…",
    resolution_title: "الحل (يُستخدم عند الموافقة فقط)",
    free_return_desc: "— يتحمل الصالون الشحن · يصدر رمز RMA",
    customer_paid_desc: "— يتحمل العميل الشحن",
    send_email: "إرسال بريد قرار للعميل",
    save_notes: "حفظ الملاحظات", under_review: "قيد المراجعة",
    reject: "رفض", approve: "موافقة",
    updated: "تم تحديث الإرجاع", failed_to_load: "فشل تحميل الإرجاع",
    inspection_passed_toast: "نجح الفحص — بدأ الاسترداد",
    inspection_failed_toast: "فشل الفحص",
    refund_failed_toast: "تعذّر معالجة الاسترداد — راجع التفاصيل",
    rma: "RMA",
  },
};

const dicts = { en, de, ar };

/* ─────────────────────────── translator ─────────────────────────── */

function pickPath(obj, parts) {
  let node = obj;
  for (const p of parts) {
    if (node == null || typeof node !== "object") return null;
    node = node[p];
  }
  return typeof node === "string" ? node : null;
}

function interpolate(str, vars) {
  if (!vars) return str;
  return str.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : `{${k}}`));
}

export function translate(lang, key, vars) {
  const parts = String(key || "").split(".");
  const primary = pickPath(dicts[lang] || dicts.en, parts);
  if (primary != null) return interpolate(primary, vars);
  const fallback = pickPath(dicts.en, parts);
  if (fallback != null) return interpolate(fallback, vars);
  return key;
}

/* ─────────────────────────── provider ─────────────────────────── */

const AdminI18nContext = createContext(null);

export function AdminI18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const stored = typeof window !== "undefined" ? localStorage.getItem(LS_KEY) : null;
    return ADMIN_LANGS.some(l => l.code === stored) ? stored : "en";
  });
  const dir = ADMIN_LANGS.find(l => l.code === lang)?.dir || "ltr";
  const isRTL = dir === "rtl";

  const setLang = useCallback((code) => {
    if (!ADMIN_LANGS.some(l => l.code === code)) return;
    setLangState(code);
    try { localStorage.setItem(LS_KEY, code); } catch { /* noop */ }
  }, []);

  // Apply dir on <html> for the whole admin surface. Because a parent
  // `AppProvider` also mutates `document.documentElement.dir` on its own mount
  // (using the public-site language stored in `ct01_lang`), a simple
  // setAttribute inside useEffect gets overwritten — child effects run before
  // parent effects. We install a MutationObserver that immediately reverts
  // any external override while the admin surface is mounted, then restore
  // the prior value on unmount.
  useEffect(() => {
    const html = document.documentElement;
    const prevDir = html.getAttribute("dir");
    const prevLang = html.getAttribute("lang");
    const apply = () => {
      if (html.getAttribute("dir") !== dir) html.setAttribute("dir", dir);
      if (html.getAttribute("lang") !== lang) html.setAttribute("lang", lang);
    };
    apply();
    const obs = new MutationObserver(apply);
    obs.observe(html, { attributes: true, attributeFilter: ["dir", "lang"] });
    return () => {
      obs.disconnect();
      if (html.getAttribute("dir") === dir) html.setAttribute("dir", prevDir || "ltr");
      if (html.getAttribute("lang") === lang) html.setAttribute("lang", prevLang || "de");
    };
  }, [dir, lang]);

  const t = useCallback((key, vars) => translate(lang, key, vars), [lang]);

  const value = useMemo(() => ({ lang, setLang, dir, isRTL, t, languages: ADMIN_LANGS }), [lang, setLang, dir, isRTL, t]);

  return <AdminI18nContext.Provider value={value}>{children}</AdminI18nContext.Provider>;
}

export function useAdminT() {
  const ctx = useContext(AdminI18nContext);
  if (!ctx) throw new Error("useAdminT must be used inside AdminI18nProvider");
  return ctx;
}
